import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { RadioButton } from 'primeng/radiobutton';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { DateUtils } from '@/shared/utils/date-utils';
import { RequerimentoRenovacaoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/requerimento-renovacao-pj/requerimento-renovacao-pj.service';
import { ReportRequerimentoRenovacaoPjRequest, ReportRequerimentoRenovacaoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/requerimento-renovacao-pj/requerimento-renovacao-pj.model';
import { RequerimentoRenovacaoPjJobService } from '@/core/services/requerimento-renovacao-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportRequerimentoRenovacaoPjRequest;
type Resp = ReportRequerimentoRenovacaoPjResponse;

interface RequerimentoRenovacaoPjFormControls {
    dataAlteracao: FormControl<Date | null>;
    corpoClinicoAlterado: FormControl<boolean>;
}

@Component({
    selector: 'app-requerimento-renovacao-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, DatePicker, RadioButton, RelatorioContainer, CanDirective],
    templateUrl: './requerimento-renovacao-pj.html',
    styleUrl: './requerimento-renovacao-pj.scss'
})
/**
 * Relatório PDF assíncrono: Requerimento de Renovação de Certificado de Inscrição PJ (prestador).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`) e os campos do requerimento
 * (data da última alteração + situação do corpo clínico).
 */
export class ReportRequerimentoRenovacaoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly requerimentoService = inject(RequerimentoRenovacaoPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly requerimentoJobService = inject(RequerimentoRenovacaoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.requerimentoJobService.job$;

    form: FormGroup<RequerimentoRenovacaoPjFormControls> = this.formBuilder.group<RequerimentoRenovacaoPjFormControls>({
        dataAlteracao: new FormControl<Date | null>(null),
        corpoClinicoAlterado: new FormControl<boolean>(false, { nonNullable: true })
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.requerimentoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.form.invalid) return null;

        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o requerimento.'
            });
            return null;
        }

        const { dataAlteracao, corpoClinicoAlterado } = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            dataAlteracao: this.formatarData(dataAlteracao),
            corpoClinicoAlterado: !!corpoClinicoAlterado
        };
    }

    protected exportar(request: Req) {
        return this.requerimentoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.form.reset({ dataAlteracao: null, corpoClinicoAlterado: false });
        this.limparJob();
    }

    private formatarData(data: Date | string | null): string | undefined {
        // Reutiliza o pipeline testado (DateUtils.formatDateToApi -> yyyy-MM-dd, com
        // componentes locais) e reformata para dd/MM/yyyy por manipulacao de string
        // pura, evitando qualquer deslocamento de fuso (off-by-one) ou valor
        // inesperado (ex.: string) vindo do datepicker.
        const iso = DateUtils.formatDateToApi(data);
        if (!iso) {
            return undefined;
        }
        const [ano, mes, dia] = iso.split('-');
        return `${dia}/${mes}/${ano}`;
    }
}
